/**
 * Role Packages — named multi-role assignment sets.
 *
 * A package is a curated collection of job role slugs that a workspace admin
 * can assign to a user or group in one action. Packages do NOT add new roles
 * to the system — they compose the existing 30-role catalog (jobRoles.ts).
 *
 * Packages are read-only definitions (no DB table yet). Assignment is performed
 * by iterating slugs and calling createRoleAssignmentRpc for each. The DB
 * stores individual role_assignments, not the package itself.
 */

export type RolePackageSlug =
  | 'intake-desk'
  | 'backlog-hygiene'
  | 'scoring-office'
  | 'session-crew'
  | 'cycle-launch'
  | 'governance-pair'
  | 'governance-full'
  | 'cycle-oversight'
  | 'read-only-insight'
  | 'platform-config'
  // ── Persona packages (cross-functional, org-role keyed) ───────────────────
  | 'persona-engineer'
  | 'persona-product-designer'
  | 'persona-product-manager'
  | 'persona-project-manager'
  | 'persona-operations'
  | 'persona-product-director'
  | 'persona-product-vp';

export interface RolePackageDefinition {
  slug: RolePackageSlug;
  displayName: string;
  description: string;
  /**
   * All job-role slugs included in this package (primary + supporting combined).
   * All must exist in DEFAULT_JOB_ROLES (jobRoles.ts).
   * When a package defines `primaryRoleSlug`, those get `tier: 'primary'` on
   * assignment; the remainder get `tier: 'supporting'`.
   */
  roleSlug: string[];
  /**
   * Subset of `roleSlug` that defines the user's operating mode — the roles
   * that anchor the focused UI view. Roles in `roleSlug` but not here are
   * "supporting": the user is capable but these roles don't drive the interface.
   *
   * Omit for functional packages where all roles are equally weighted.
   * Required for persona packages (theme: 'cross-functional').
   */
  primaryRoleSlug?: string[];
  /**
   * Short phrase surfaced in cards and tooltips.
   * Describes who typically receives this package.
   */
  typicalPersona: string;
  /**
   * Optional tag group for visual grouping on the packages page.
   * Loosely maps to job family themes (not a formal FK).
   */
  theme: 'backlog' | 'measurement' | 'sessions' | 'cycles' | 'governance' | 'platform' | 'cross-functional';
}

export const ROLE_PACKAGES: RolePackageDefinition[] = [
  // ── Backlog ────────────────────────────────────────────────────────────────

  {
    slug: 'intake-desk',
    displayName: 'Intake Desk',
    description:
      'Full front-door coverage: submit ideas, shape candidates, triage incoming requests, and transfer ownership. Assign to PMO intake coordinators or product operations staff.',
    roleSlug: ['idea-submitter', 'item-manager', 'item-triage'],
    typicalPersona: 'PMO intake coordinator, product ops',
    theme: 'backlog',
  },
  {
    slug: 'backlog-hygiene',
    displayName: 'Backlog Hygiene',
    description:
      'Keep the backlog clean and connected: shape items, curate relationships, and manage deduplication. Assign to portfolio analysts or senior PMs responsible for backlog quality.',
    roleSlug: ['item-manager', 'relationship-curator'],
    typicalPersona: 'Portfolio analyst, senior PM',
    theme: 'backlog',
  },

  // ── Measurement ────────────────────────────────────────────────────────────

  {
    slug: 'scoring-office',
    displayName: 'Scoring Office',
    description:
      'Full measurement authority: define criteria and scales, govern value expressions, select items for evaluation, collect values, and accept or override results. Assign to a central scoring team or PMO measurement office.',
    roleSlug: [
      'criteria-authority',
      'criteria-value-authority',
      'item-selection-authority',
      'values-collector',
      'values-approver',
    ],
    typicalPersona: 'PMO measurement team',
    theme: 'measurement',
  },

  // ── Sessions ───────────────────────────────────────────────────────────────

  {
    slug: 'session-crew',
    displayName: 'Session Crew',
    description:
      'Design, run, and participate in ranking sessions. Assign to the team responsible for setting up and facilitating prioritization sessions.',
    roleSlug: ['session-designer', 'session-facilitator', 'session-participant'],
    typicalPersona: 'Facilitation team',
    theme: 'sessions',
  },

  // ── Cycles ─────────────────────────────────────────────────────────────────

  {
    slug: 'cycle-launch',
    displayName: 'Cycle Launch',
    description:
      'Own the full cycle machinery: design cycles, own tracks, own and publish priority lists. Assign to the cycle owner and their extended team for a new prioritization cycle.',
    roleSlug: ['cycle-designer', 'track-owner', 'priority-list-owner'],
    typicalPersona: 'Cycle owner + extended team',
    theme: 'cycles',
  },

  // ── Governance ─────────────────────────────────────────────────────────────

  {
    slug: 'governance-pair',
    displayName: 'Governance Pair',
    description:
      'The core catchball partnership: one Ball Holder proposes, one Priority Ratifier ratifies. Assign together to the two parties who negotiate at an organizational level.',
    roleSlug: ['ball-holder', 'decision-authority'],
    typicalPersona: 'Division lead + VP (or equivalent)',
    theme: 'governance',
  },
  {
    slug: 'governance-full',
    displayName: 'Governance Full',
    description:
      'Complete governance coverage: propose, negotiate, approve, and ratify. Assign to a governance team that includes both contributors and ratifiers.',
    roleSlug: [
      'ball-holder',
      'catchball-contributor',
      'priority-setter',
      'priority-approver',
      'decision-authority',
    ],
    typicalPersona: 'Full governance committee',
    theme: 'governance',
  },
  {
    slug: 'cycle-oversight',
    displayName: 'Cycle Oversight',
    description:
      'Track adherence to ratified priorities and flag drift. Assign to executives or PMO leaders who monitor the plan of record without running cycle mechanics.',
    roleSlug: ['plan-of-record-manager'],
    typicalPersona: 'CPO, PMO director, exec sponsor',
    theme: 'governance',
  },

  // ── Platform ───────────────────────────────────────────────────────────────

  {
    slug: 'read-only-insight',
    displayName: 'Read-Only Insight',
    description:
      'View analytics, session results, and alignment reports without taking any action. Assign to executives, board observers, or external stakeholders who need visibility only.',
    roleSlug: ['reports-analyst', 'strategist'],
    typicalPersona: 'Exec stakeholder, board observer',
    theme: 'platform',
  },
  {
    slug: 'platform-config',
    displayName: 'Platform Config',
    description:
      'Design and maintain workflow templates at workspace level. Assign to the process architect or ops lead responsible for standardizing how work flows through the system.',
    roleSlug: ['workflow-designer'],
    typicalPersona: 'Process architect, ops lead',
    theme: 'platform',
  },

  // ── Persona packages (cross-functional, org-role keyed) ──────────────────
  // primaryRoleSlug anchors the user's operating mode; the remainder are
  // supporting roles they can perform without those roles driving their UI.

  {
    slug: 'persona-engineer',
    displayName: 'Engineer',
    description:
      'Participate in ranking sessions and submit ideas. Supporting access to value collection and analytics. Assign to individual contributors who need a focused, low-noise interface.',
    roleSlug: ['session-participant', 'idea-submitter', 'values-collector', 'reports-analyst'],
    primaryRoleSlug: ['session-participant', 'idea-submitter'],
    typicalPersona: 'Software engineer, IC contributor',
    theme: 'cross-functional',
  },
  {
    slug: 'persona-product-designer',
    displayName: 'Product Designer',
    description:
      'Submit ideas, manage backlog items, and participate in sessions. Supporting access to value collection and relationship curation.',
    roleSlug: ['idea-submitter', 'item-manager', 'session-participant', 'values-collector', 'relationship-curator'],
    primaryRoleSlug: ['idea-submitter', 'item-manager', 'session-participant'],
    typicalPersona: 'UX / product designer',
    theme: 'cross-functional',
  },
  {
    slug: 'persona-product-manager',
    displayName: 'Product Manager',
    description:
      'Own the backlog for a product area, facilitate sessions, and propose in catchball. Supporting access to triage, values, relationships, and reporting.',
    roleSlug: [
      'item-manager', 'session-facilitator', 'ball-holder', 'priority-setter',
      'item-triage', 'values-collector', 'relationship-curator', 'session-participant', 'reports-analyst',
    ],
    primaryRoleSlug: ['item-manager', 'session-facilitator', 'ball-holder', 'priority-setter'],
    typicalPersona: 'Product manager, squad PM',
    theme: 'cross-functional',
  },
  {
    slug: 'persona-project-manager',
    displayName: 'Project Manager',
    description:
      'Manage items and triage, design sessions, and own priority lists. Supporting access to relationship curation, reporting, and plan-of-record monitoring.',
    roleSlug: [
      'item-manager', 'item-triage', 'session-designer', 'priority-list-owner',
      'relationship-curator', 'values-collector', 'reports-analyst', 'plan-of-record-manager',
    ],
    primaryRoleSlug: ['item-manager', 'item-triage', 'session-designer', 'priority-list-owner'],
    typicalPersona: 'Project manager, program coordinator',
    theme: 'cross-functional',
  },
  {
    slug: 'persona-operations',
    displayName: 'Operations',
    description:
      'Run backlog operations: manage items, triage intake, design workflows, and curate relationships. Supporting access to value approval and reporting.',
    roleSlug: [
      'item-manager', 'item-triage', 'workflow-designer', 'relationship-curator',
      'values-approver', 'criteria-value-authority', 'reports-analyst',
    ],
    primaryRoleSlug: ['item-manager', 'item-triage', 'workflow-designer', 'relationship-curator'],
    typicalPersona: 'Product ops, PMO operations',
    theme: 'cross-functional',
  },
  {
    slug: 'persona-product-director',
    displayName: 'Product Director',
    description:
      'Own governance within a product domain: propose in catchball, set and approve priorities, design cycles. Supporting access to selection authority, ratification, plan-of-record, and reporting.',
    roleSlug: [
      'ball-holder', 'priority-setter', 'priority-approver', 'cycle-designer',
      'item-selection-authority', 'decision-authority', 'plan-of-record-manager', 'session-facilitator', 'reports-analyst',
    ],
    primaryRoleSlug: ['ball-holder', 'priority-setter', 'priority-approver', 'cycle-designer'],
    typicalPersona: 'Director of Product, Group PM',
    theme: 'cross-functional',
  },
  {
    slug: 'persona-product-vp',
    displayName: 'Product VP',
    description:
      'Ratify priorities, monitor the plan of record, and approve priority lists. Supporting access to analytics, strategy, catchball proposals, and priority setting.',
    roleSlug: [
      'decision-authority', 'plan-of-record-manager', 'priority-approver',
      'reports-analyst', 'strategist', 'ball-holder', 'priority-setter',
    ],
    primaryRoleSlug: ['decision-authority', 'plan-of-record-manager', 'priority-approver'],
    typicalPersona: 'VP Product, CPO',
    theme: 'cross-functional',
  },
];

const PACKAGE_BY_SLUG = new Map(ROLE_PACKAGES.map((p) => [p.slug, p]));

export function getRolePackage(slug: RolePackageSlug): RolePackageDefinition | undefined {
  return PACKAGE_BY_SLUG.get(slug);
}

export function getRolePackagesByTheme(
  theme: RolePackageDefinition['theme'],
): RolePackageDefinition[] {
  return ROLE_PACKAGES.filter((p) => p.theme === theme);
}

export const ROLE_PACKAGE_THEME_LABELS: Record<RolePackageDefinition['theme'], string> = {
  backlog: 'Backlog',
  measurement: 'Measurement',
  sessions: 'Sessions',
  cycles: 'Cycles',
  governance: 'Governance',
  platform: 'Platform',
  'cross-functional': 'Cross-functional',
};

/**
 * Returns the tier ('primary' | 'supporting') for a given slug within a package.
 * Packages without `primaryRoleSlug` treat all slugs as 'supporting'.
 */
export function getRoleSlugTier(
  pkg: RolePackageDefinition,
  slug: string,
): 'primary' | 'supporting' {
  if (!pkg.primaryRoleSlug || pkg.primaryRoleSlug.length === 0) return 'supporting';
  return pkg.primaryRoleSlug.includes(slug) ? 'primary' : 'supporting';
}
